import {
  AUTO_UPDATE_VALUE,
  ProductGroupFormValue,
  ProductGroupReadDTO,
  ProductGroupType
} from 'core/limitation/ProductGroup';
import {
  DefaultProductGroupManager,
  ProductGroupManager,
  toServerPayload
} from 'core/limitation/ProductGroupManager';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  buildValueLabelMap,
  createSelectOptionsFromEnum
} from 'utils/SelectOptionsUtils';
import { defaultTo, get, isEmpty, omit, omitBy } from 'lodash';
import { validateEmpty } from 'utils/ValidateUtils';
import coreContext, { useCoreContext } from 'contexts/coreContext';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager
} from 'core/adRequestSource/AdRequestSourceManager';
import { PMaxCore } from 'core';
import { isSystemAdmin } from 'helper/ActorHelper';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  buildOutdoorDeliveryTargetOptions,
  getOutdoorDeliveryUnit,
  getOutdoorExtraGetter
} from './utils';

const defaultProductGroupManager: ProductGroupManager =
  new DefaultProductGroupManager();
const defaultAdRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();

const validate = (
  value: ProductGroupFormValue
): ValidationResult<ProductGroupFormValue> => {
  return omitBy(
    {
      groupName: validateEmpty(value.groupName),
      groupValues: validateEmpty([
        ...value.groupValues,
        ...value.customGroupValues
      ])
    },
    isEmpty
  );
};

export type ProductGroupFormData = {
  title: string;
  loading: boolean;
  initProductGroup?: ProductGroupFormValue;
  productGroupTypeOptions: SelectOptions[];
  spaceOptions: SelectOptions[];
  domainOptions: SelectOptions[];
  setSpaceOptions: Dispatch<SetStateAction<SelectOptions[]>>;
  setDomainOptions: Dispatch<SetStateAction<SelectOptions[]>>;
  validate: (
    values: ProductGroupFormValue
  ) => ValidationResult<ProductGroupFormValue>;
  save: (values: ProductGroupFormValue) => Promise<void>;
};

export const useCreateProductGroupFormModel = (
  productGroupManager: ProductGroupManager = defaultProductGroupManager
): ProductGroupFormData => {
  const core = useContext(coreContext);
  const agencyIds =
    core &&
    core.authenticationManager.actor &&
    core.authenticationManager.actor.agencyId
      ? [core.authenticationManager.actor.agencyId]
      : [];

  const initProductGroup: ProductGroupFormValue = {
    groupName: '',
    groupType: ProductGroupType.DOMAIN,
    description: '',
    groupValues: [],
    customGroupValues: [],
    agencyIds
  };

  const { loading, callAPIs } = useCallAPI();
  const [spaceOptions, setSpaceOptions] = useState<SelectOptions[]>([]);
  const [domainOptions, setDomainOptions] = useState<SelectOptions[]>([]);
  const productGroupTypeOptions = useMemo(
    () =>
      createSelectOptionsFromEnum(ProductGroupType, 'productGroupForm.labels.'),
    []
  );

  const save = useCallback(
    async (saveValue: ProductGroupFormValue) => {
      return callAPIs([
        () =>
          productGroupManager.createProductGroup(
            toServerPayload(
              saveValue,
              saveValue.groupType === ProductGroupType.ADSPACE
                ? spaceOptions.slice(2)
                : domainOptions
            )
          )
      ]);
    },
    [productGroupManager, spaceOptions, domainOptions, callAPIs]
  );

  return {
    title: i18n.t('productGroupForm.labels.createTitle'),
    loading: loading,
    initProductGroup,
    productGroupTypeOptions,
    spaceOptions,
    domainOptions,
    setSpaceOptions,
    setDomainOptions,
    validate,
    save
  };
};

export const useEditProductGroupFormModel = (
  productGroupId: number,
  productGroupManager: ProductGroupManager = defaultProductGroupManager,
  adRequestSourceManager: AdRequestSourceManager = defaultAdRequestSourceManager
): ProductGroupFormData => {
  const [initProductGroup, setInitProductGroup] = useState<
    ProductGroupFormValue | undefined
  >(undefined);

  const { loading, callAPIs } = useCallAPI();
  const [spaceOptions, setSpaceOptions] = useState<SelectOptions[]>([]);
  const [domainOptions, setDomainOptions] = useState<SelectOptions[]>([]);
  const [outdoorLabelMaps, setOutdoorLabelMaps] = useState<{
    storeNum?: Map<string | number, string>;
    deviceCharacteristic?: Map<string | number, string>;
  }>({});
  const core = useCoreContext() as PMaxCore;
  const isSysAdmin = isSystemAdmin(core.authenticationManager.actor);

  const productGroupTypeOptions = useMemo(
    () =>
      createSelectOptionsFromEnum(ProductGroupType, 'productGroupForm.labels.'),
    []
  );

  const setUpInitProductGroup = useCallback(
    (
      productGroup: ProductGroupReadDTO,
      storeNumLabelMap?: Map<string | number, string>,
      deviceCharacteristicLabelMap?: Map<string | number, string>
    ) => {
      const rawAttributes = defaultTo(productGroup.attributes, {});
      const isSpace = productGroup.groupType === ProductGroupType.ADSPACE;
      const deliveryUnit = getOutdoorDeliveryUnit(
        productGroup.adFormat,
        productGroup.channelGroupId
      );
      const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
      const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;
      const isAutoUpdate = productGroup.autoUpdate;
      const isStoreAutoUpdate = isStoreUnit && isEmpty(rawAttributes.storeNum);
      const isDeviceAutoUpdate =
        isDeviceUnit && isEmpty(rawAttributes.deviceCharacteristic);
      let customGroupValues: SelectOptions[] = productGroup.customGroupValues;
      let attributes: { [key: string]: string[] };
      if (isStoreUnit && rawAttributes.storeNum) {
        // Reconstruct customGroupValues from storeNum; strip storeNum from filter attributes
        const restAttributes = { ...rawAttributes };
        delete restAttributes.storeNum;
        customGroupValues = rawAttributes.storeNum.map((storeNum: string) => ({
          label: storeNumLabelMap?.get(storeNum) ?? storeNum,
          value: storeNum
        }));
        attributes = restAttributes;
      } else if (isDeviceUnit && rawAttributes.deviceCharacteristic) {
        // Reconstruct customGroupValues from deviceCharacteristic; strip it from filter attributes
        const restAttributes = { ...rawAttributes };
        delete restAttributes.deviceCharacteristic;
        customGroupValues = rawAttributes.deviceCharacteristic.map(
          (value: string) => ({
            label: deviceCharacteristicLabelMap?.get(value) ?? value,
            value
          })
        );
        attributes = restAttributes;
      } else {
        attributes = rawAttributes;
      }

      let groupValues;
      if (isStoreUnit) {
        groupValues = isStoreAutoUpdate
          ? [
              {
                label: i18n.t('productGroupForm.labels.conditionStore'),
                value: AUTO_UPDATE_VALUE
              }
            ]
          : [];
      } else if (isDeviceUnit) {
        groupValues = isDeviceAutoUpdate
          ? [
              {
                label: i18n.t('productGroupForm.labels.conditionDevice'),
                value: AUTO_UPDATE_VALUE
              }
            ]
          : [];
      } else {
        groupValues = isAutoUpdate
          ? [
              {
                label: i18n.t('productGroupForm.labels.conditionSpace'),
                value: AUTO_UPDATE_VALUE
              }
            ]
          : get(productGroup, 'groupValues', []);
      }

      setInitProductGroup(
        isSpace
          ? {
              ...omit(productGroup, ['id', 'state', 'autoUpdate']),
              groupType: ProductGroupType.ADSPACE,
              agencyIds: get(productGroup, 'agencyIds', []),
              groupValues,
              customGroupValues,
              adFormat: defaultTo(productGroup.adFormat, ''),
              channel: productGroup.channel as string,
              attributes
            }
          : {
              ...productGroup,
              groupType: ProductGroupType.DOMAIN,
              agencyIds: get(productGroup, 'agencyIds', []),
              groupValues: get(productGroup, 'groupValues', []),
              attributes: undefined
            }
      );
    },
    []
  );

  useEffect(() => {
    callAPIs(
      [
        productGroupManager.getProductGroupById.bind(
          productGroupManager,
          productGroupId,
          false
        )
      ],
      async (productGroup: ProductGroupReadDTO) => {
        // Fetch outdoor attribute bag so we can resolve labels for saved
        // storeNum / deviceCharacteristic values when reconstructing
        // customGroupValues. Other adFormats don't use them, so the fetch is
        // skipped.
        let storeNumLabelMap: Map<string | number, string> | undefined;
        let deviceCharacteristicLabelMap:
          | Map<string | number, string>
          | undefined;
        if (
          productGroup.adFormat === 'Outdoor' &&
          productGroup.channelGroupId
        ) {
          const bag = await adRequestSourceManager.getOutdoorSpaceAttributes(
            productGroup.channelGroupId
          );
          storeNumLabelMap = buildValueLabelMap(bag.storeNum ?? []);
          deviceCharacteristicLabelMap = buildValueLabelMap(
            bag.deviceCharacteristic ?? []
          );
          setOutdoorLabelMaps({
            storeNum: storeNumLabelMap,
            deviceCharacteristic: deviceCharacteristicLabelMap
          });
        }
        setUpInitProductGroup(
          productGroup,
          storeNumLabelMap,
          deviceCharacteristicLabelMap
        );
      }
    );
  }, [
    isSysAdmin,
    productGroupManager,
    productGroupId,
    adRequestSourceManager,
    setUpInitProductGroup,
    callAPIs
  ]);

  useEffect(() => {
    if (
      initProductGroup?.groupType !== ProductGroupType.ADSPACE ||
      !initProductGroup.attributes
    ) {
      return;
    }

    const isOutdoor = initProductGroup.adFormat === 'Outdoor';
    const deliveryUnit = getOutdoorDeliveryUnit(
      initProductGroup.adFormat,
      initProductGroup.channelGroupId
    );
    const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
    const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;

    callAPIs(
      [
        () =>
          adRequestSourceManager.getSpacesWithConditions(
            isSysAdmin,
            initProductGroup.attributes,
            initProductGroup.adFormat,
            initProductGroup.channelGroupId,
            isOutdoor ? getOutdoorExtraGetter(isStoreUnit) : undefined
          )
      ],
      (spaces: SelectOptions[]) => {
        if (!isOutdoor) {
          setSpaceOptions(spaces);
          return;
        }
        setSpaceOptions(
          buildOutdoorDeliveryTargetOptions(spaces, {
            isStoreUnit,
            isDeviceUnit,
            labelMap: isStoreUnit
              ? outdoorLabelMaps.storeNum
              : outdoorLabelMaps.deviceCharacteristic
          })
        );
      }
    );
  }, [
    initProductGroup,
    isSysAdmin,
    adRequestSourceManager,
    outdoorLabelMaps,
    callAPIs
  ]);

  const save = useCallback(
    async (saveValue: ProductGroupFormValue) => {
      return callAPIs([
        () =>
          productGroupManager.updateProductGroup({
            id: productGroupId,
            ...toServerPayload(
              saveValue,
              saveValue.groupType === ProductGroupType.ADSPACE
                ? spaceOptions.slice(2)
                : domainOptions
            )
          })
      ]);
    },
    [productGroupId, productGroupManager, spaceOptions, domainOptions, callAPIs]
  );

  return {
    title: i18n.t('productGroupForm.labels.editTitle'),
    loading: loading,
    initProductGroup,
    productGroupTypeOptions,
    spaceOptions,
    domainOptions,
    setSpaceOptions,
    setDomainOptions,
    validate,
    save
  };
};
